import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { hasCap } from "../utils/capabilities";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getTenants, getTenantLocations } from "../api/tenantApi";
import { getEdgesSummary, listEdges } from "../api/edgeApi";
import { toast } from "react-toastify";
import ClaimDialog from "../components/EdgeExplorer/ClaimDialog";
import MacList from "../components/EdgeExplorer/MacList";
import BulkMoveDialog from "../components/EdgeExplorer/BulkMoveDialog";
import OtaApprovalDialog from "../components/EdgeExplorer/OtaApprovalDialog";
import { certInfo, certExpiringSoon } from "../components/EdgeExplorer/otaHelpers";
import ChannelDialog from "../components/EdgeExplorer/ChannelDialog";
import {
  NO_LOCATION,
  buildTenantForest,
  decodeNode,
  edgeLabel,
  encodeNode,
  formatLastSeen,
  locationCounts,
  locationNodeId,
  mergeLocations,
  mergeSummaries,
  resolveNode,
  rollupBadges,
  stackLabel,
  tenantCounts,
  tenantNodeId,
  withNoneBucket,
} from "../components/EdgeExplorer/treeHelpers";

const PAGE_SIZE = 50;
const TENANT_PAGE = 200; // backend max per page
const MAX_TENANT_PAGES = 10;

const CSS = `
.ex-body{display:flex;gap:20px;align-items:flex-start}
.ex-card{background:var(--bg-card);border:1px solid var(--border);border-radius:10px;box-shadow:var(--shadow-sm)}
.ex-tree{width:296px;flex-shrink:0;padding:14px 10px;display:flex;flex-direction:column;gap:2px}
.ex-th{padding:0 8px 8px;font-size:12px;font-weight:600;color:var(--text-muted);letter-spacing:.06em;text-transform:uppercase}
.ex-nd{display:flex;flex-wrap:wrap;align-items:center;gap:4px 8px;min-height:36px;padding:4px 8px;border-radius:8px;font-size:14px;color:var(--text-primary);cursor:pointer}
.ex-nd:hover{background:var(--bg-hover)}
.ex-nd.sel{background:var(--info-bg);color:var(--info-text);font-weight:600}
.ex-nd .n{flex:1 1 90px;min-width:90px;overflow:hidden;white-space:nowrap;text-overflow:ellipsis}
.ex-bd{flex-shrink:0;font-size:11px;font-weight:600;padding:2px 8px;border-radius:999px;border:1px solid;white-space:nowrap}
.ex-ok{color:#276749;background:#f0fff4;border-color:#c6f6d5}
.ex-off{color:#c53030;background:#fff5f5;border-color:#fed7d7}
.ex-warn{color:#975a16;background:#fffaf0;border-color:#fbd38d}
.ex-main{flex:1;min-width:0;display:flex;flex-direction:column;gap:16px}
.ex-ttl h1{margin:0;font-size:22px;font-weight:700;color:var(--accent-navy)}
.ex-kk{font-size:12px;color:var(--text-muted)}
.ex-kpis{display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:14px}
.ex-kpi{padding:14px 16px}
.ex-kpi .l{font-size:11.5px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;color:var(--text-muted)}
.ex-kpi .v{font-size:28px;font-weight:700;color:var(--btn-primary);margin-top:4px}
.ex-kpi .s{font-size:12px;color:var(--text-muted);margin-top:2px}
.ex-tb{display:flex;align-items:center;gap:10px}
.ex-bt{height:36px;padding:0 14px;border-radius:8px;border:1px solid var(--border-strong);background:var(--bg-card);font-size:13px;font-weight:500;display:inline-flex;align-items:center;gap:8px;color:var(--text-primary);cursor:pointer;font-family:inherit}
.ex-bt.pri{background:var(--btn-primary);border-color:var(--btn-primary);color:#fff}
.ex-bt:disabled{opacity:.55;cursor:not-allowed}
.ex-tbl{overflow:hidden}
.ex-tr{display:grid;grid-template-columns:2.4fr 1.2fr 1.2fr 1.9fr .8fr .7fr;align-items:center;min-height:56px;padding:0 20px;border-top:1px solid var(--border-subtle);font-size:14px;cursor:pointer}
.ex-tr:hover{background:var(--bg-hover)}
.ex-tr.h{min-height:40px;border-top:0;background:var(--bg-surface);font-size:11.5px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;color:var(--text-muted);cursor:default}
.ex-tr.h:hover{background:var(--bg-surface)}
.ex-sort{all:unset;display:inline-flex;align-items:center;gap:4px;cursor:pointer;text-transform:inherit;letter-spacing:inherit;font:inherit;color:inherit}
.ex-sort:hover,.ex-sort:focus-visible,.ex-sort.on{color:var(--text-primary)}
.ex-sort:focus-visible{outline:2px solid var(--btn-primary);outline-offset:2px;border-radius:4px}
.ex-sub{font-size:12px;color:var(--text-muted);margin-top:1px}
.ex-dt{display:inline-block;width:8px;height:8px;border-radius:4px;margin-right:8px}
.ex-empty{display:flex;justify-content:center;padding:24px 0}
.ex-ec{width:640px;max-width:100%;padding:36px 40px;display:flex;flex-direction:column;align-items:center;text-align:center;gap:14px}
.ex-ico{width:56px;height:56px;border-radius:14px;background:var(--info-bg);border:1px solid var(--info-border);color:var(--accent-navy-light);display:flex;align-items:center;justify-content:center}
.ex-steps{display:flex;flex-direction:column;gap:10px;text-align:left;width:100%;margin-top:6px}
.ex-st{display:flex;gap:12px;align-items:flex-start;font-size:14px;color:var(--text-muted)}
.ex-st .num{width:24px;height:24px;border-radius:12px;background:var(--btn-primary);color:#fff;font-size:12px;font-weight:600;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.ex-info{width:100%;padding:10px 14px;border-radius:8px;background:var(--info-bg);border:1px solid var(--info-border);color:var(--info-text);font-size:13px;text-align:left;line-height:1.5}
.ex-notice{padding:8px 12px;border-radius:8px;background:var(--info-bg);border:1px solid var(--info-border);color:var(--info-text);font-size:13px}
.ex-in{height:36px;padding:0 12px;border-radius:8px;border:1px solid var(--border-strong);background:var(--bg-card);color:var(--text-primary);font-size:13px;font-family:inherit;min-width:220px}
.ex-tr.bulk{grid-template-columns:36px 2.4fr 1.2fr 1.2fr 1.9fr .8fr .7fr}
.ex-bar{display:flex;align-items:center;gap:10px;padding:10px 16px;background:var(--bg-card);border:1px solid var(--border);border-radius:10px;box-shadow:var(--shadow-sm)}
.ex-tr.cert{grid-template-columns:2.4fr 1.2fr 1.2fr 1.9fr 1.3fr .8fr .7fr}
.ex-tr.bulk.cert{grid-template-columns:36px 2.4fr 1.2fr 1.2fr 1.9fr 1.3fr .8fr .7fr}
.ex-tr.more{justify-content:center;grid-template-columns:1fr;text-align:center}
`;
const Chevron = ({ open }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d={open ? "M6 9l6 6 6-6" : "M9 6l6 6-6 6"} />
  </svg>
);

const Badges = ({ counts }) =>
  rollupBadges(counts).map((b) => (
    <span key={b.kind} className={`ex-bd ex-${b.kind}`}>{b.text}</span>
  ));

const CertPill = ({ edge }) => {
  const c = certInfo(edge);
  return <span>{c.kind === "none" ? "—" : <span className={`ex-bd ex-${c.kind}`} title={c.sub}>{c.text}</span>}</span>;
};

const Dot = ({ online }) => (
  <span className="ex-dt" style={{ background: online ? "var(--success)" : "var(--danger)", margin: 0 }} />
);

// VPN tüneli buluttan ayrı: cihaz buluta ulaşırken VPN "retrying"de takılabilir. Etiketler: edges.vpn.<durum>
const VPN_STATES = ["connected", "retrying", "offline"];
const VPN_COLOR = { connected: "var(--success)", retrying: "var(--danger)", offline: "var(--danger)" };

// Tenants are few (unlike edges): plain fetch-all is fine here.
async function fetchAllTenants(token) {
  const items = [];
  for (let i = 0; i < MAX_TENANT_PAGES; i++) {
    const res = await getTenants(token, { limit: TENANT_PAGE, offset: i * TENANT_PAGE });
    items.push(...(res.items || []));
    if (items.length >= (res.total ?? 0) || !(res.items || []).length) break;
  }
  return items;
}

export default function EdgeExplorer() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const accessToken = useSelector((s) => s.session?.accessToken);
  const me = useSelector((s) => s.session?.me);
  const isSuper = me?.global_role === "superadmin";
  const canClaim = me?.global_role === "superadmin" || me?.tenant_role === "admin";
  const canRegister = hasCap(me, "edge.add_manual") && hasCap(me, "edge.claim");
  const myTenantId = me?.tenant_id || me?.tenantId || null;

  const [tenants, setTenants] = useState(null);
  const [summary, setSummary] = useState(null); // merged index or null while unknown
  const [locs, setLocs] = useState({}); // tenantId -> [{id,name}]
  const [expanded, setExpanded] = useState({});
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState({ key: "", dir: "asc" }); // server-side: the list is paginated
  // ?q= comes from the global search ("see all results")
  const [qInput, setQInput] = useState(() => params.get("q") || "");
  const [q, setQ] = useState(() => (params.get("q") || "").trim());
  const [claimOpen, setClaimOpen] = useState(false);
  const [picked, setPicked] = useState({}); // edge id -> {id,name}
  const [bulkDlg, setBulkDlg] = useState(null); // "ota" | "channel" | "move"
  const [edgeState, setEdgeState] = useState({ key: null, items: [], total: 0, loading: true, error: null });
  const locLoading = useRef({});
  const reqId = useRef(0);

  const { roots, childrenOf } = useMemo(() => buildTenantForest(tenants || []), [tenants]);
  const tenantById = useMemo(() => Object.fromEntries((tenants || []).map((t) => [t.id, t])), [tenants]);

  // ---- selection lives in the URL (?node=...) ----
  const selected = tenants ? resolveNode(decodeNode(params.get("node")), tenantById, roots[0]?.id, locs) : null;
  const selKey = selected ? encodeNode(selected) : null;

  useEffect(() => {
    if (selected?.type === "edge") navigate(`/edges/${selected.id}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selKey]);

  // ---- tenants (fallback: the user's own tenant as the only root) ----
  useEffect(() => {
    let alive = true;
    const own = () => (myTenantId ? [{ id: myTenantId, name: me?.tenant_name || t("edges.tree.ownTenantName"), parent_tenant_id: null }] : []);
    fetchAllTenants(accessToken)
      .then((list) => {
        if (list.length) return list;
        throw new Error("empty");
      })
      .catch(() => {
        if (alive) toast.warning(t("edges.errors.tenantsFailed"));
        return own();
      })
      .then((list) => alive && setTenants(list));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  // ---- summary: one call per root subtree ----
  const loadSummary = useCallback(() => {
    if (!roots.length) return;
    Promise.all(roots.map((r) => getEdgesSummary(accessToken, r.id).catch(() => null))).then((all) => {
      if (all.every((s) => !s)) toast.error(t("edges.errors.summaryFailed"));
      else setSummary(mergeSummaries(all));
    });
  }, [accessToken, roots, t]);
  useEffect(loadSummary, [loadSummary]);

  // ---- expand the path to the selected node ----
  useEffect(() => {
    if (!selected || selected.type === "edge") return;
    setExpanded((e) => {
      const n = { ...e };
      for (let t = tenantById[selected.tenantId]; t; t = tenantById[t.parent_tenant_id]) n[tenantNodeId(t.id)] = true;
      if (selected.type === "location") n[locationNodeId(selected.tenantId, selected.locationId)] = true;
      return n;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selKey]);

  // ---- locations of expanded tenants (lazy) ----
  useEffect(() => {
    Object.keys(expanded).forEach((id) => {
      if (!expanded[id] || !id.startsWith("t:")) return;
      const tid = id.slice(2);
      if (locs[tid] || locLoading.current[tid]) return;
      locLoading.current[tid] = true;
      getTenantLocations(accessToken, tid, { limit: 100 })
        .then((r) => r.items || [])
        .catch(() =>
          // fallback only when the locations call fails: derive from the tenant's edges
          listEdges(accessToken, { tenant_id: tid, limit: 200 }).then((r) => mergeLocations(null, r.items)).catch(() => [])
        )
        .then((list) => setLocs((c) => ({ ...c, [tid]: list })));
    });
  }, [expanded, locs, accessToken]);

  // ---- search debounce ----
  useEffect(() => {
    const h = setTimeout(() => setQ(qInput.trim()), 300);
    return () => clearTimeout(h);
  }, [qInput]);

  // The global search can send us here while the Explorer is already open: follow ?q=
  const urlQ = params.get("q");
  useEffect(() => {
    if (urlQ !== null) setQInput(urlQ);
  }, [urlQ]);

  // ---- edges of the selected node: lazy, paginated, server-side filtered ----
  const curKey = selKey && `${selKey}|${q}|${status}|${sort.key}:${sort.dir}`;
  const fetchPage = useCallback(
    async (offset) => {
      if (!selected || selected.type === "edge") return;
      const id = ++reqId.current;
      setEdgeState((s) => ({ ...s, key: curKey, loading: true, error: null, ...(offset === 0 ? { items: [], total: 0 } : {}) }));
      try {
        const p = { tenant_id: selected.tenantId, limit: PAGE_SIZE, offset };
        if (selected.type === "location") p.location_id = selected.locationId;
        // a tenant node lists its whole subtree, matching the summed badge/KPI numbers
        if (selected.type === "tenant") p.include_subtree = true;
        if (q) p.q = q;
        if (status !== "all") p.status = status;
        if (sort.key) { p.sort = sort.key; p.order = sort.dir; }
        const r = await listEdges(accessToken, p);
        if (id !== reqId.current) return;
        setEdgeState((s) => ({ key: curKey, items: offset ? [...s.items, ...r.items] : r.items, total: r.total, loading: false, error: null }));
      } catch (err) {
        if (id !== reqId.current) return;
        const message = err.response?.data?.detail || t("edges.errors.listFailed");
        setEdgeState((s) => ({ ...s, loading: false, error: message }));
        toast.error(message);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [curKey, accessToken]
  );
  useEffect(() => {
    fetchPage(0);
  }, [fetchPage]);

  // selection clears on node change
  useEffect(() => {
    setPicked({});
  }, [selKey]);

  const pickedList = Object.values(picked);
  const toggleEdge = (e) =>
    setPicked((p) => {
      const n = { ...p };
      if (n[e.id]) delete n[e.id];
      else n[e.id] = { id: e.id, name: edgeLabel(e) };
      return n;
    });

  const pick = (node) => {
    const key = encodeNode(node);
    if (key === selKey) {
      const id = node.type === "tenant" ? tenantNodeId(node.tenantId) : locationNodeId(node.tenantId, node.locationId);
      setExpanded((e) => ({ ...e, [id]: !e[id] }));
    } else {
      setParams({ node: key }); // push: back/forward restores the previous selection
    }
  };

  const isSel = (type, tid, lid) =>
    selected && selected.type === type && selected.tenantId === tid && (type === "tenant" || selected.locationId === lid);

  const edgeRows = edgeState.key === curKey ? edgeState.items : [];

  const renderTenant = (tn, depth) => {
    const id = tenantNodeId(tn.id);
    const open = !!expanded[id];
    const pad = 8 + depth * 18;
    return (
      <React.Fragment key={id}>
        <div className={`ex-nd${isSel("tenant", tn.id) ? " sel" : ""}`} style={{ paddingLeft: pad }} onClick={() => pick({ type: "tenant", tenantId: tn.id })}>
          <Chevron open={open} />
          <span className="n">{depth === 0 ? <b>{tn.name}</b> : tn.name}</span>
          <Badges counts={tenantCounts(summary, tn.id, childrenOf)} />
        </div>
        {open && (childrenOf[tn.id] || []).map((c) => renderTenant(c, depth + 1))}
        {open && !locs[tn.id] && <div className="ex-kk" style={{ paddingLeft: pad + 22 }}>{t("edges.loading")}</div>}
        {open && locs[tn.id] && withNoneBucket(locs[tn.id], summary, tn.id).map((l) => renderLocation(tn, l, depth + 1))}
      </React.Fragment>
    );
  };

  const renderLocation = (tn, l, depth) => {
    const id = locationNodeId(tn.id, l.id);
    const open = !!expanded[id];
    const pad = 8 + depth * 18;
    return (
      <React.Fragment key={id}>
        <div className={`ex-nd${isSel("location", tn.id, l.id) ? " sel" : ""}`} style={{ paddingLeft: pad }} onClick={() => pick({ type: "location", tenantId: tn.id, locationId: l.id })}>
          <Chevron open={open} />
          <span className="n">{l.name}</span>
          <Badges counts={locationCounts(summary, tn.id, l.id)} />
        </div>
        {open && isSel("location", tn.id, l.id) &&
          edgeRows.map((e) => (
            <div key={e.id} className="ex-nd" style={{ paddingLeft: pad + 22 }} onClick={() => navigate(`/edges/${e.id}`)}>
              <Dot online={e.is_online === true} />
              <span className="n">{edgeLabel(e)}</span>
            </div>
          ))}
      </React.Fragment>
    );
  };

  // ---- right side ----
  const selTenant = selected && selected.type !== "edge" && tenantById[selected.tenantId];
  let title = "";
  let caption = "";
  let counts = null;
  if (selTenant) {
    if (selected.type === "location") {
      const loc = (locs[selected.tenantId] || []).find((l) => l.id === selected.locationId);
      title = loc?.name || (selected.locationId === NO_LOCATION ? t("edges.tree.noLocation") : "");
      caption = t("edges.tree.captionLocation", { tenant: selTenant.name });
      counts = locationCounts(summary, selected.tenantId, selected.locationId);
    } else {
      title = selTenant.name;
      caption = selTenant.parent_tenant_id && tenantById[selTenant.parent_tenant_id] ? t("edges.tree.captionSubTenant", { parent: tenantById[selTenant.parent_tenant_id].name }) : t("edges.tree.captionTenant");
      counts = tenantCounts(summary, selTenant.id, childrenOf);
    }
  }
  const dash = (v) => (counts ? v : "—");
  const kpis = [
    { id: "edge", l: t("edges.kpi.edge"), v: dash(counts?.edge_count), s: counts ? t("edges.kpi.online", { n: counts.online }) : "—" },
    { id: "offline", l: t("edges.kpi.offline"), v: dash(counts?.offline), s: counts ? t("edges.kpi.within", { n: counts.edge_count }) : "—", color: counts?.offline ? "var(--danger)" : undefined },
    { id: "alarms", l: t("edges.kpi.openAlarms"), v: dash(counts?.open_alarms), s: counts?.open_alarms ? t("edges.kpi.needsAttention") : t("edges.kpi.noOpenAlarm"), color: counts?.open_alarms ? "#975a16" : undefined },
  ];
  if (isSuper) {
    const soon = edgeRows.filter(certExpiringSoon).length;
    kpis.push({ id: "cert", l: t("edges.kpi.certSoon"), v: soon, s: t("edges.kpi.onThisPage"), color: soon ? "#975a16" : undefined });
  }

  const isLoc = selected?.type === "location";
  const claimLocation = isLoc && selected.locationId !== NO_LOCATION ? selected.locationId : null;
  // Edge ekleme sadece bir Lokasyon seçiliyken görünüyordu; bir Müşteri/Alt
  // müşteri seçiliyken (varsayılan görünüm) ya da "Lokasyonsuz" grubundayken
  // buton hiç yoktu — registerEdge location_id'siz de çalışıyor, sadece
  // hangi tenant'a ekleneceğini bilmesi yeterli.
  const canAddEdge = canRegister && !!selected?.tenantId;
  // click: ascending, again: descending, again: back to the default order (newest first)
  const toggleSort = (key) =>
    setSort((cur) => (cur.key !== key ? { key, dir: "asc" } : cur.dir === "asc" ? { key, dir: "desc" } : { key: "", dir: "asc" }));
  const sortHeader = (key, label) => (
    <span aria-sort={sort.key === key ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
      <button type="button" className={`ex-sort${sort.key === key ? " on" : ""}`} onClick={() => toggleSort(key)} aria-label={t("edges.table.sortBy", { column: label })}>
        {label}
        <span aria-hidden="true">{sort.key === key ? (sort.dir === "asc" ? "▲" : "▼") : ""}</span>
      </button>
    </span>
  );
  const fresh = edgeState.key === curKey;
  const allPicked = edgeRows.length > 0 && edgeRows.every((e) => picked[e.id]);
  const toggleAll = () =>
    setPicked((p) => {
      if (allPicked) return {};
      const n = { ...p };
      edgeRows.forEach((e) => (n[e.id] = { id: e.id, name: edgeLabel(e) }));
      return n;
    });
  const isEmptyLocation = isLoc && fresh && !edgeState.loading && !edgeState.error && edgeState.total === 0 && !q && status === "all";

  if (!tenants) return <div className="ex-kk">{t("edges.loading")}</div>;

  return (
    <div>
      <style>{CSS}</style>
      <div className="ex-body">
        <div className="ex-card ex-tree">
          <div className="ex-th">{t("edges.tree.title")}</div>
          {roots.length === 0 && <div className="ex-kk" style={{ padding: "0 8px" }}>{t("edges.tree.noTenants")}</div>}
          {roots.map((t) => renderTenant(t, 0))}
        </div>
        <div className="ex-main">
          <div className="ex-ttl">
            <div className="ex-kk">{caption}</div>
            <h1>{title}</h1>
          </div>

          {isEmptyLocation ? (
            <div className="ex-empty">
              <div className="ex-card ex-ec">
                <div className="ex-ico">
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="6" rx="1.5" /><rect x="3" y="14" width="18" height="6" rx="1.5" /><path d="M7 7h.01M7 17h.01" /></svg>
                </div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "var(--accent-navy)" }}>{t("edges.empty.title")}</div>
                <div className="ex-kk" style={{ fontSize: 14, maxWidth: 420 }}>{t("edges.empty.text")}</div>
                {canAddEdge && <button type="button" className="ex-bt pri" onClick={() => setClaimOpen(true)}>{t("edges.empty.generate")}</button>}
                <div className="ex-steps">
                  <div className="ex-st"><span className="num">1</span><span>{t("edges.empty.step1")}</span></div>
                  <div className="ex-st"><span className="num">2</span><span>{t("edges.empty.step2")}</span></div>
                  <div className="ex-st"><span className="num">3</span><span>{t("edges.empty.step3")}</span></div>
                </div>
                <div className="ex-info">{t("edges.empty.info")}</div>
              </div>
            </div>
          ) : (
            <>
              {canClaim && pickedList.length > 0 && (
                <div className="ex-bar" role="toolbar" aria-label={t("edges.bulk.toolbarAria")}>
                  <b>{t("edges.bulk.selected", { count: pickedList.length })}</b>
                  <span style={{ flex: 1 }} />
                  <button type="button" className="ex-bt" onClick={() => setBulkDlg("move")}>{t("edges.bulk.move")}</button>
                  <button type="button" className="ex-bt" onClick={() => setBulkDlg("channel")}>{t("edges.bulk.channel")}</button>
                  <button type="button" className="ex-bt pri" onClick={() => setBulkDlg("ota")}>{t("edges.bulk.approveOta")}</button>
                </div>
              )}
              <div className="ex-kpis">
                {kpis.map((k) => (
                  <div key={k.id} className="ex-card ex-kpi">
                    <div className="l">{k.l}</div>
                    <div className="v" style={k.color ? { color: k.color } : undefined}>{k.v}</div>
                    <div className="s">{k.s}</div>
                  </div>
                ))}
              </div>
              <div className="ex-tb">
                <input className="ex-in" type="search" placeholder={t("edges.toolbar.searchPlaceholder")} aria-label={t("edges.toolbar.searchAria")} value={qInput} onChange={(e) => setQInput(e.target.value)} />
                <select className="ex-bt" value={status} onChange={(e) => setStatus(e.target.value)} aria-label={t("edges.toolbar.statusAria")}>
                  <option value="all">{t("edges.toolbar.statusAll")}</option>
                  <option value="online">{t("edges.toolbar.statusOnline")}</option>
                  <option value="offline">{t("edges.toolbar.statusOffline")}</option>
                </select>
                <span style={{ flex: 1 }} />
                {canAddEdge && (
                  <button type="button" className="ex-bt pri" onClick={() => setClaimOpen(true)}>{t("edges.toolbar.addEdge")}</button>
                )}
              </div>
              <div className="ex-card ex-tbl">
                <div className={`ex-tr h${canClaim ? " bulk" : ""}${isSuper ? " cert" : ""}`}>{canClaim && <span><input type="checkbox" aria-label={t("edges.table.selectAll")} checked={allPicked} onChange={toggleAll} /></span>}{sortHeader("edge", t("edges.table.edge"))}{sortHeader("status", t("edges.table.status"))}{sortHeader("last_seen", t("edges.table.lastSeen"))}{sortHeader("os_stack", t("edges.table.osStack"))}{isSuper && sortHeader("cert", t("edges.table.cert"))}{sortHeader("asset", t("edges.table.asset"))}{sortHeader("alarm", t("edges.table.alarm"))}</div>
                {!edgeState.error && fresh && !edgeState.loading && edgeRows.length === 0 && <div className="ex-tr"><span className="ex-kk">{t("edges.table.noMatch")}</span></div>}
                {edgeRows.map((e) => (
                  <div key={e.id} className={`ex-tr${canClaim ? " bulk" : ""}${isSuper ? " cert" : ""}`} onClick={() => navigate(`/edges/${e.id}`)}>
                    {canClaim && <span onClick={(ev) => ev.stopPropagation()}><input type="checkbox" aria-label={t("edges.table.selectEdge", { name: edgeLabel(e) })} checked={!!picked[e.id]} onChange={() => toggleEdge(e)} /></span>}
                    <span>
                      <b>{edgeLabel(e)}</b>
                      <div className="ex-sub">{[e.locationName, (e.valonix_serial || e.serial_number) && t("edges.table.serial", { serial: e.valonix_serial || e.serial_number })].filter(Boolean).join(" · ")}</div>
                      <MacList edge={e} compact />
                    </span>
                    <span>
                      <div><span className="ex-dt" style={{ background: e.is_online ? "var(--success)" : "var(--danger)" }} />{t("edges.table.cloud", { state: e.is_online ? t("edges.table.cloudOnline") : t("edges.table.cloudOffline") })}</div>
                      <div className="ex-sub" title={e.vpn_status_since ? t("edges.table.since", { since: formatLastSeen(e.vpn_status_since) }) : undefined}>
                        <span className="ex-dt" style={{ background: VPN_COLOR[e.vpn_status] || "var(--text-muted)" }} />{t("edges.table.vpn", { state: VPN_STATES.includes(e.vpn_status) ? t(`edges.vpn.${e.vpn_status}`) : "—" })}
                      </div>
                    </span>
                    <span>{formatLastSeen(e.last_seen)}</span>
                    <span>{stackLabel(e)}</span>
                    {isSuper && <CertPill edge={e} />}
                    <span>{e.asset?.assetId || "—"}</span>
                    <span>{e.open_alarm_count > 0 ? <span className="ex-bd ex-warn">{e.open_alarm_count}</span> : "—"}</span>
                  </div>
                ))}
                {edgeState.loading && <div className="ex-tr"><span className="ex-kk">{t("edges.loading")}</span></div>}
                {!edgeState.loading && fresh && edgeRows.length < edgeState.total && (
                  <div className="ex-tr more">
                    <span><button type="button" className="ex-bt" onClick={() => fetchPage(edgeRows.length)}>{t("edges.table.loadMore", { shown: edgeRows.length, total: edgeState.total })}</button></span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      <OtaApprovalDialog
        open={bulkDlg === "ota"}
        edges={pickedList}
        onClose={(changed) => {
          setBulkDlg(null);
          if (changed) fetchPage(0);
        }}
      />
      <ChannelDialog
        open={bulkDlg === "channel"}
        edges={pickedList}
        onClose={(changed) => {
          setBulkDlg(null);
          if (changed) fetchPage(0);
        }}
      />
      <ClaimDialog
        open={claimOpen}
        tenantId={selected?.tenantId}
        locationId={claimLocation}
        onClose={() => setClaimOpen(false)}
        onCreated={() => {
          loadSummary();
          fetchPage(0);
        }}
      />
      <BulkMoveDialog
        open={bulkDlg === "move"}
        tenantId={selected?.tenantId}
        edges={pickedList}
        accessToken={accessToken}
        onClose={() => setBulkDlg(null)}
        onMoved={() => {
          setPicked({});
          loadSummary();
          fetchPage(0);
        }}
      />
    </div>
  );
}
